import GoogleAnalytics from '../utils/GoogleAnalytics';

// Page performance tracking interface
interface PagePerformanceData {
  path: string;
  loadTime: number;
  renderTime: number;
  timestamp: number;
}

class AnalyticsService {
  private pageLoadStartTime: number = 0;
  private pagePerformanceData: PagePerformanceData[] = [];
  private debugMode: boolean = false;

  /**
   * Check if debug mode is enabled
   */
  private checkDebugMode(): boolean {
    if (typeof window === 'undefined') return false;

    // Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('debug_mode') || urlParams.has('debug')) {
      return true;
    }

    // Check localStorage
    const localStorageDebug = localStorage.getItem('ga_debug_mode');
    if (localStorageDebug === 'true') {
      return true;
    }

    // Check environment variable (for development)
    if (process.env.NODE_ENV === 'development') {
      return true;
    }

    return false;
  }

  /**
   * Enable or disable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    if (typeof window !== 'undefined') {
      localStorage.setItem('ga_debug_mode', enabled.toString());
    }
    console.log(`🔍 Analytics debug mode ${enabled ? 'ENABLED' : 'DISABLED'}`);
  }

  /**
   * Track page view with loading time metrics using existing GoogleAnalytics utility
   */
  trackPageView(path: string, loadTime?: number, renderTime?: number): void {
    const performanceData: PagePerformanceData = {
      path,
      loadTime: loadTime || 0,
      renderTime: renderTime || 0,
      timestamp: Date.now(),
    };

    // Store performance data
    this.pagePerformanceData.push(performanceData);

    // Track page load time event using existing GoogleAnalytics utility
    if (loadTime) {
      GoogleAnalytics.triggerPageLoadTimeEvent(path, Math.round(loadTime));
    }

    // Enhanced logging
    if (this.debugMode) {
      console.log(`📊 ANALYTICS: Page Load Time Tracked`);
      console.log(`📍 Path: ${path}`);
      console.log(`⏱️  Load Time: ${Math.round(loadTime || 0)}ms`);
      console.log(`🎨 Render Time: ${Math.round(renderTime || 0)}ms`);
    } else {
      console.log(
        `Page load time tracked: ${path} (Load: ${loadTime}ms, Render: ${renderTime}ms)`
      );
    }
  }

  /**
   * Start timing page load
   */
  startPageLoadTiming(): void {
    this.pageLoadStartTime = typeof window !== 'undefined' && typeof window.performance !== 'undefined' ? window.performance.now() : Date.now();
  }

  /**
   * End timing page load and track the page view
   */
  endPageLoadTimingAndTrack(path: string, renderTime?: number): void {
    if (this.pageLoadStartTime === 0) {
      console.warn(
        'Page load timing not started. Call startPageLoadTiming() first.'
      );
      return;
    }

    const loadTime = typeof window !== 'undefined' && typeof window.performance !== 'undefined' ? window.performance.now() - this.pageLoadStartTime : 0;
    this.trackPageView(path, loadTime, renderTime);
    this.pageLoadStartTime = 0;
  }

  /**
   * Track custom event using existing GoogleAnalytics utility
   */
  trackEvent(
    eventName: string,
    category: string,
    _label?: string,
    _value?: number
  ): void {
    GoogleAnalytics.triggerEvent(category, eventName);
  }

  /**
   * Get performance metrics for all tracked pages
   */
  getPerformanceData(): PagePerformanceData[] {
    return [...this.pagePerformanceData];
  }

  /**
   * Get average loading time for a specific path
   */
  getAverageLoadTime(path: string): number {
    const pathData = this.pagePerformanceData.filter(
      (data) => data.path === path
    );
    if (pathData.length === 0) return 0;

    const totalLoadTime = pathData.reduce(
      (sum, data) => sum + data.loadTime,
      0
    );
    return totalLoadTime / pathData.length;
  }

  /**
   * Get performance summary for all pages
   */
  getPerformanceSummary(): Record<
    string,
    { avgLoadTime: number; avgRenderTime: number; count: number }
  > {
    const summary: Record<
      string,
      { avgLoadTime: number; avgRenderTime: number; count: number }
    > = {};

    this.pagePerformanceData.forEach((data) => {
      if (!summary[data.path]) {
        summary[data.path] = { avgLoadTime: 0, avgRenderTime: 0, count: 0 };
      }

      summary[data.path].avgLoadTime += data.loadTime;
      summary[data.path].avgRenderTime += data.renderTime;
      summary[data.path].count++;
    });

    // Calculate averages
    Object.keys(summary).forEach((path) => {
      const { count } = summary[path];
      summary[path].avgLoadTime = Math.round(summary[path].avgLoadTime / count);
      summary[path].avgRenderTime = Math.round(
        summary[path].avgRenderTime / count
      );
    });

    return summary;
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// Export types for external use
export type { PagePerformanceData };
